// temporalFieldFormat.js

import { formatAnnualDateValue, isAnnualDateFieldType } from 'utilities/AnnualDate';
import {
  isDatePrecision,
  isUtcDateOnlyInstant,
  normalizeDateValue,
  toDatePrecisionLocalDay,
} from 'utilities/DateTime';
import { formatRecurrenceHint } from 'utilities/DateTimeRangeRecurrence';

// Shared temporal display formatting used across list/board/gallery/table.

export const isTemporalFieldType = (fieldType) => (
  fieldType === 'annualdate'
  || fieldType === 'date'
  || fieldType === 'daterange'
  || fieldType === 'datetime'
  || fieldType === 'datetimerange'
);

export const isFirestoreTimestampObject = (value) => (
  typeof value === 'object'
  && (
    (value?.seconds != null && value?.nanoseconds != null)
    || (value?._seconds != null && value?._nanoseconds != null)
  )
);

const normalizeStartDate = (value, isTimestampObject) => {
  if (value?.toDate && typeof value.toDate === 'function') {
    return normalizeDateValue(value.toDate());
  }

  if (isTimestampObject) {
    return normalizeDateValue(value);
  }

  if (value && typeof value === 'object' && value.startDate) {
    return normalizeDateValue(value.startDate?.toDate ? value.startDate.toDate() : value.startDate);
  }

  return normalizeDateValue(value);
};

const normalizeEndDate = (value) => {
  if (!value || typeof value !== 'object' || !value.endDate) return null;
  return normalizeDateValue(value.endDate?.toDate ? value.endDate.toDate() : value.endDate);
};

const formatDateOnlyCalendar = (date, locale, options = {}) => {
  const formatOptions = { ...options };
  if (isUtcDateOnlyInstant(date)) {
    formatOptions.timeZone = 'UTC';
  }
  return date.toLocaleDateString(locale, formatOptions);
};

const formatDatePrecisionDay = (date, locale, options = {}) => {
  const localDay = toDatePrecisionLocalDay(date) || date;
  return localDay.toLocaleDateString(locale, options);
};

export const formatTemporalFieldValue = ({
  value,
  fieldType,
  locale = 'en-US',
  dateOptions,
  dateTimeOptions,
  rangeSeparator = ' – ',
}) => {
  if (isAnnualDateFieldType(fieldType)) {
    const formattedValue = formatAnnualDateValue(value, { locale });
    return formattedValue || null;
  }

  const isTimestampValue = isFirestoreTimestampObject(value);
  const temporalType = isTemporalFieldType(fieldType) ? fieldType : (isTimestampValue ? 'date' : '');
  if (!temporalType) return null;

  const startDate = normalizeStartDate(value, isTimestampValue);
  if (!startDate) return null;

  if (temporalType === 'datetime') {
    return startDate.toLocaleDateString(locale, dateTimeOptions);
  }

  if (temporalType === 'datetimerange') {
    const startIsDateOnly = isDatePrecision(value?.startPrecision);
    const endIsDateOnly = isDatePrecision(value?.endPrecision || value?.startPrecision);
    const dateOnlyOptions = dateOptions || {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    const startText = startIsDateOnly
      ? formatDatePrecisionDay(startDate, locale, dateOnlyOptions)
      : startDate.toLocaleDateString(locale, dateTimeOptions);
    const endDate = normalizeEndDate(value);
    const rangeText = endDate
      ? `${startText}${rangeSeparator}${
        endIsDateOnly
          ? formatDatePrecisionDay(endDate, locale, dateOnlyOptions)
          : endDate.toLocaleDateString(locale, dateTimeOptions)
      }`
      : startText;
    const recurrenceHint = value?.recurrence ? formatRecurrenceHint(value.recurrence) : '';
    return recurrenceHint ? `${rangeText} · ${recurrenceHint}` : rangeText;
  }

  if (temporalType === 'daterange') {
    const startText = formatDateOnlyCalendar(startDate, locale, dateOptions);
    const endDate = normalizeEndDate(value);
    if (endDate) {
      const endText = formatDateOnlyCalendar(endDate, locale, dateOptions);
      return `${startText}${rangeSeparator}${endText}`;
    }
    return startText;
  }

  return formatDateOnlyCalendar(startDate, locale, dateOptions);
};
